import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { AppThunk } from "appThunk";
import { apiGet, apiPost, ApiResponseStatus } from "modules/helpers/api/apiSlice";
import { notifyError, notifySuccess } from "modules/notifications/notificationsSlice";
import { RootState } from "store";

interface User {
    id: string,
    firstName: string,
    lastName: string,
    email: string,
    role: UserRole
}

enum UserRole {
    Unknown = "Unknown",
    Owner = "Owner",
    Admin = "Admin",
    Editor = "Editor",
    Viewer = "Viewer"
}

interface UsersVisibility {
    isVisible: boolean,
    accountId: string
}

interface OwnerForm {
    firstName: string,
    lastName: string,
    email: string,
    errors: OwnerFormErrors
}

interface OwnerFormErrors {
    firstName: string,
    lastName: string,
    email: string
}

interface AddOwnerVisibility {
    isVisible: boolean,
    accountId: string
}

interface UsersState {
    users: User[],
    usersVisibility: UsersVisibility,
    owner: OwnerForm,
    addOwnerVisibility: AddOwnerVisibility
}

const initialState: UsersState = {
    users: [],
    usersVisibility: {
        isVisible: false,
        accountId: ""
    },
    owner: {
        firstName: "",
        lastName: "",
        email: "",
        errors: {
            firstName: "",
            lastName: "",
            email: ""
        }
    },
    addOwnerVisibility: {
        isVisible: false,
        accountId: ""
    }
};

const usersSlice = createSlice({
    name: "admin/accounts/users",
    initialState,
    reducers: {
        setUsers: (state, action: PayloadAction<User[]>) => {
            state.users = action.payload;
        },
        clearUsers: (state) => {
            state.users = initialState.users;
        },
        showUsers: (state, action: PayloadAction<string>) => {
            state.usersVisibility.isVisible = true;
            state.usersVisibility.accountId = action.payload;
        },
        hideUsers: (state) => {
            state.usersVisibility = initialState.usersVisibility;
        },
        setOwner: (state, action: PayloadAction<OwnerForm>) => {
            state.owner = action.payload;
        },
        clearOwner: (state) => {
            state.owner = initialState.owner;
        },
        showAddOwner: (state, action: PayloadAction<string>) => {
            state.addOwnerVisibility.isVisible = true;
            state.addOwnerVisibility.accountId = action.payload;
        },
        hideAddOwner: (state) => {
            state.addOwnerVisibility = initialState.addOwnerVisibility;
        }
    }
});

export const {
    clearUsers,
    showUsers,
    hideUsers,
    setOwner,
    clearOwner,
    showAddOwner,
    hideAddOwner,
} = usersSlice.actions;

export const getUsers = (accountId: string): AppThunk => async (dispatch) => {
    const response = await dispatch(apiGet(`/api/admin/accounts/${accountId}/users`));
    switch (response.status) {
        case ApiResponseStatus.Ok: {
            const users = response.data.users;
            dispatch(usersSlice.actions.setUsers(users));
            break;
        }
        default: {
            dispatch(usersSlice.actions.clearUsers());
            break;
        }
    }
};

export const addOwner = (accountId: string): AppThunk => async (dispatch, getState) => {
    const state = getState();
    const owner = selectOwner(state);
    const response = await dispatch(apiPost(`/api/admin/accounts/${accountId}/users`, owner));
    switch (response.status) {
        case ApiResponseStatus.Ok: {
            dispatch(usersSlice.actions.hideAddOwner());
            dispatch(usersSlice.actions.clearOwner());
            dispatch(notifySuccess("Owner added."));
            dispatch(getUsers(accountId));
            break;
        }
        case ApiResponseStatus.NotFound: {
            dispatch(notifyError("Account not found."));
            break;
        }
        case ApiResponseStatus.BadRequest: {
            const errors = {
                firstName: response.errorData?.errors?.firstName?.[0],
                lastName: response.errorData?.errors?.lastName?.[0],
                email: response.errorData?.errors?.email?.[0]
            };
            dispatch(usersSlice.actions.setOwner({ ...owner, errors }));
            break;
        }
        default: {
            break;
        }
    }
};

export const selectUsers = (state: RootState) => {
    return state.admin.accounts.users.users;
};

export const selectUsersVisibility = (state: RootState) => {
    return state.admin.accounts.users.usersVisibility;
};

export const selectOwner = (state: RootState) => {
    return state.admin.accounts.users.owner;
};

export const selectAddOwnerVisibility = (state: RootState) => {
    return state.admin.accounts.users.addOwnerVisibility;
};

export default usersSlice;
